import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, useAuthStore } from '@/store/authStore';
import { normalizeUser } from './normalizeUser';

/**
 * Single typed HTTP layer for the whole app. All resource modules
 * (competitions.ts, submissions.ts, etc.) go through this instance so the
 * auth/refresh behavior and base URL live in exactly one place - if the real
 * backend's response envelope differs from our assumptions, this is the only
 * file that needs to change.
 */
export const apiClient = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

let refreshPromise: Promise<string | null> | null = null;

/** Runs `task` with no other tab refreshing at the same time (the server rotates the cookie on every refresh). */
function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  return typeof navigator !== 'undefined' && navigator.locks
    ? (navigator.locks.request('aperture-refresh', task) as Promise<T>)
    : task();
}

/**
 * Redeems the httpOnly refresh cookie for a new access token. Resolves to the
 * token, or null when there is no valid session (which also signs this tab out).
 */
export async function refreshSession(): Promise<string | null> {
  // De-dupe concurrent 401s: only one /auth/refresh call in flight at a time.
  if (!refreshPromise) {
    refreshPromise = withRefreshLock(() => axios.post('/api/v1/auth/refresh'))
      .then((res) => {
        const { accessToken, user } = res.data;
        useAuthStore.getState().setSession({
          accessToken,
          user: user ? normalizeUser(user) : useAuthStore.getState().user!,
        });
        return accessToken as string;
      })
      .catch((error: unknown) => {
        // Only a rejected cookie ends the session; a network blip or 5xx must not log the user out.
        if (axios.isAxiosError(error) && error.response && error.response.status < 500) {
          // No broadcast: a dead cookie is found by every tab on its own, and a stale tab
          // announcing it would sign out an account that has just been signed in elsewhere.
          useAuthStore.getState().clearSession({ broadcast: false });
        }
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;

    if (error.response?.status === 401 && original && !original._retried && !original.url?.includes('/auth/')) {
      original._retried = true;
      const newToken = await refreshSession();
      if (newToken) {
        original.headers.set('Authorization', `Bearer ${newToken}`);
        return apiClient(original);
      }
    }

    return Promise.reject(error);
  },
);

/**
 * Extracts a human-readable message from an API error, tolerant of several
 * possible backend shapes.
 *
 * Aperture.Api's ExceptionHandlingMiddleware always sends RFC 7807
 * ProblemDetails, but the actual specific reason lives in different fields
 * depending on exception type - `errors` (a field -> message[] map) for
 * ValidationAppException, `detail` for NotFoundException/
 * ForbiddenException/DomainException. `title` is always just the generic
 * category ("One or more validation errors occurred.", "Business rule
 * violation", etc.), so it must be checked LAST, only as a fallback when
 * nothing more specific is present - checking it first (as this used to)
 * meant every meaningful backend error message was silently replaced by that
 * generic title.
 */
export function getApiErrorMessage(error: unknown, fallback = 'Prišlo je do napake. Poskusite znova.'): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string; detail?: string; title?: string; error?: string; errors?: Record<string, string[]> }
      | undefined;
    if (data?.message) return data.message;
    if (data?.errors) {
      const first = Object.values(data.errors)[0];
      if (Array.isArray(first) && first.length) return first[0];
    }
    if (data?.detail) return data.detail;
    if (data?.error) return data.error;
    if (error.response?.status === 401) return 'Napačni podatki za prijavo ali seja je potekla.';
    if (error.response?.status === 403) return 'Nimate dovoljenja za to dejanje.';
    if (error.response?.status === 404) return 'Ni najdeno.';
    if (error.message === 'Network Error') return 'Strežnika ni mogoče doseči. Poskusite kasneje.';
    if (data?.title) return data.title;
  }
  return fallback;
}
